import React, {useCallback, useEffect, useState} from 'react';
import {Box, CircularProgress, Drawer, IconButton, Skeleton, Typography} from '@mui/material';
import {styled} from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FolderIcon from '@mui/icons-material/Folder';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {useApiService} from '../../../context/ApiServiceContext';
import Asset, {isVideoAsset} from '../../../models/Asset';
import Folder from '../../../models/Folder';

const PER_PAGE = 24;

type FolderItem = (Folder | Asset) & { itemType: 'folder' | 'asset' };

const SheetPaper = {
    sx: {
        borderRadius: '18px 18px 0 0',
        background: 'linear-gradient(220deg, #051726 36%, #025989 100%)',
        padding: 2,
        height: '70vh',
    },
};

const TileGrid = styled(Box)(({theme}) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    // Explicit row sizing + NO align-content: iOS 26 Safari mis-sizes implicit
    // rows in a scrollable grid when align-content is start/flex-start — rows
    // came out ~8px shorter than the tiles, so every row overlapped the next
    // (measured on the 26.2 simulator; gridtest bisect). max-content rows with
    // the default (normal) alignment measure correctly on every engine tried.
    gridAutoRows: 'max-content',
    gridAutoFlow: 'row',
    gap: theme.spacing(1),
    overflowY: 'auto',
    flexGrow: 1,
    paddingBottom: theme.spacing(2),
}));

// Square via the padding-top hack, NOT aspect-ratio: Ben's device rendered the
// grid ragged (tiles collapsing to each image's natural height). padding-top
// percentages resolve against the tile WIDTH on every engine, so the square
// can't break; content then fills the reserved box absolutely.
const Tile = styled(Box)({
    position: 'relative',
    paddingTop: '100%',
    borderRadius: '12px',
    overflow: 'hidden',
    border: '1px solid #ffffff40',
    backgroundColor: '#0a1f33',
    cursor: 'pointer',
    '&:active': {opacity: 0.7},
});

/** Fills the square the padding hack reserved; centers folder content. */
const TileContent = styled(Box)({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

interface GallerySheetProps {
    open: boolean;
    folderId: string;
    onClose: () => void;
    /** Tapping an asset applies it and closes the sheet — one tap, no confirm. */
    onSelect: (asset: Asset) => void;
    /** False hides video assets entirely — a USER LAYER is image-only, and an
     *  unpickable-looking tile that then errors is worse than no tile. */
    allowVideo?: boolean;
}

/** Simplified team-library picker: a flat tile grid with folder drill-in.
 *  No search, no folder management, no upload tile (upload lives on the
 *  action sheet) — clients just tap a picture. Mounted fresh per open (the
 *  parent conditionally renders it), so drill-in state never leaks between
 *  sessions. */
const GallerySheet: React.FC<GallerySheetProps> = ({open, folderId, onClose, onSelect, allowVideo = true}) => {
    const {t} = useTranslation();
    const apiService = useApiService();
    const [stack, setStack] = useState<string[]>([]);
    const [items, setItems] = useState<FolderItem[] | null>(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    const currentFolderId = stack[stack.length - 1] ?? folderId;

    const fetchPage = useCallback(async (folder: string, pageToFetch: number) => {
        const response = await apiService.api.get<FolderItem[]>(`/folder/${folder}`, {
            params: {page: pageToFetch, perPage: PER_PAGE},
        });
        return response.data;
    }, [apiService]);

    // (Re)load the sheet's current folder whenever it opens or the user drills in.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setItems(null);
        setPage(1);
        fetchPage(currentFolderId, 1)
            .then((data) => {
                if (cancelled) return;
                setItems(data);
                setHasMore(data.length >= PER_PAGE);
            })
            .catch(() => !cancelled && toast.error(t('error.folderLoad') || 'Could not load folder items'));
        return () => {
            cancelled = true;
        };
    }, [open, currentFolderId, fetchPage, t]);

    const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
        const el = e.currentTarget;
        if (!hasMore || loadingMore || el.scrollHeight - el.scrollTop > el.clientHeight + 60) return;
        setLoadingMore(true);
        const nextPage = page + 1;
        fetchPage(currentFolderId, nextPage)
            .then((data) => {
                setItems((prev) => [...(prev ?? []), ...data]);
                setPage(nextPage);
                setHasMore(data.length >= PER_PAGE);
            })
            .catch(() => toast.error(t('error.folderLoad') || 'Could not load folder items'))
            .finally(() => setLoadingMore(false));
    };

    const assetsAndFolders = (items ?? []).filter(
        (item) => allowVideo || item.itemType !== 'asset' || !isVideoAsset(item as Asset),
    );

    return (
        <Drawer anchor="bottom" open={open} onClose={onClose} PaperProps={SheetPaper}>
            <Box sx={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                <Box sx={{width: 36, height: 4, borderRadius: 2, backgroundColor: '#ffffff50', margin: '0 auto 12px', flexShrink: 0}}/>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexShrink: 0}}>
                    {stack.length > 0 && (
                        <IconButton size="small" sx={{color: 'white'}}
                                    onClick={() => setStack((prev) => prev.slice(0, -1))}>
                            <ArrowForwardIcon/>
                        </IconButton>
                    )}
                    <Typography sx={{color: 'white', fontWeight: 700, fontSize: '1.05rem'}}>
                        {t('editor.fillMode.galleryTitle')}
                    </Typography>
                </Box>

                <TileGrid onScroll={handleScroll}>
                    {items === null &&
                        Array.from({length: 9}).map((_, i) => (
                            // Same padding-top square as Tile (aspect-ratio broke on device).
                            <Skeleton key={i} variant="rounded"
                                      sx={{height: 0, paddingTop: '100%', bgcolor: '#ffffff20'}}/>
                        ))}

                    {items !== null && assetsAndFolders.length === 0 && (
                        <Typography sx={{color: '#ffffff90', gridColumn: '1 / -1', textAlign: 'center', mt: 4}}>
                            {t('editor.fillMode.emptyFolder')}
                        </Typography>
                    )}

                    {assetsAndFolders.map((item, index) => {
                        if (item.itemType === 'folder') {
                            const folder = item as Folder & { itemType: 'folder' };
                            return (
                                <Tile key={`folder-${folder.id}-${index}`}
                                      onClick={() => setStack((prev) => [...prev, folder.id])}>
                                    <TileContent>
                                        <Box sx={{textAlign: 'center', color: '#C3F3FD', px: 1, minWidth: 0}}>
                                            <FolderIcon sx={{fontSize: 36}}/>
                                            <Typography sx={{fontSize: '0.7rem'}} noWrap>{folder.name}</Typography>
                                        </Box>
                                    </TileContent>
                                </Tile>
                            );
                        }
                        const asset = item as Asset & { itemType: 'asset' };
                        return (
                            <Tile key={`asset-${asset.id}-${index}`} onClick={() => onSelect(asset)}
                                  data-testid="fill-gallery-asset">
                                <TileContent>
                                    <img src={asset.thumbnail} alt={asset.name} loading="lazy"
                                         style={{width: '100%', height: '100%', objectFit: 'cover'}}/>
                                    {isVideoAsset(asset) && (
                                        <PlayCircleOutlineIcon
                                            sx={{position: 'absolute', color: 'white', fontSize: 32, filter: 'drop-shadow(0 0 4px #000)'}}/>
                                    )}
                                </TileContent>
                            </Tile>
                        );
                    })}

                    {loadingMore && (
                        <Box sx={{gridColumn: '1 / -1', display: 'flex', justifyContent: 'center', py: 1}}>
                            <CircularProgress size={22} sx={{color: '#C3F3FD'}}/>
                        </Box>
                    )}
                </TileGrid>
            </Box>
        </Drawer>
    );
};

export default GallerySheet;
